import { Component, Input, OnInit } from '@angular/core';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { AppComponent } from 'app/app.component';
import { ExportFileTypeEnum, RiderVehicleType, RiderVehicleTypeEnum } from 'app/models';
import { ConfigService, DeliveryOrderService, GeoZoneService, AppDataService } from 'app/services';
import { Utils } from 'app/utils';
import moment from 'moment';
import { MenuItem, MessageService } from 'primeng/api';

enum MenuDailyReport {
    Report = 0,
    Map = 1
}

@Component({
    selector: 'report-daily-statistics',
    templateUrl: './report-daily-statistics.component.html',
    styleUrls: ['./report-daily-statistics.component.scss'],
    providers: [MessageService]
})
export class ReportDailyStatisticsComponent implements OnInit {
    public selectGeoZone: any;
    @Input() set _selectGeoZone(value: any) {
        this.selectGeoZone = value;
    }

    constructor(public deliveryOrderService: DeliveryOrderService,
        public configService: ConfigService,
        public app: AppComponent,
        public messageService: MessageService,
        private geoZoneService: GeoZoneService,
        private appData: AppDataService) { }

    public orderStatistics!: any;
    public geoPoints!: any;
    public cities!: any;
    public totalCompletedMoto: number = 0;
    public totalUnallocatedMoto: number = 0;
    public totalCompletedSedan: number = 0;
    public totalUnallocatedSedan: number = 0;
    public totalCompletedSUV: number = 0;
    public totalUnallocatedSUV: number = 0;

    public selectedDateRange: Date[] = [];
    public filterStatistics: any = {
        citiesId: null,
        vehicleTypes: [RiderVehicleTypeEnum.Moto, RiderVehicleTypeEnum.Sedan, RiderVehicleTypeEnum.SUV]
    };

    previousSelection: any[] = [RiderVehicleTypeEnum.Moto, RiderVehicleTypeEnum.Sedan, RiderVehicleTypeEnum.SUV];

    private searchTimer: number = 0;

    public vehicleTypeEnum = RiderVehicleTypeEnum;
    public vehicleTypes = RiderVehicleType.RiderVehicleTypeXpressList;

    public exportFileTypeEnum = ExportFileTypeEnum;
    public fileName: string = '';

    public menuDailyReport = MenuDailyReport;
    public menuDailyReportActive: MenuDailyReport = MenuDailyReport.Report;
    public menuItems: MenuItem[] = [
        {
            label: 'Report',
            command: (e: any) => this.handleTabChange(MenuDailyReport.Report)
        },
        {
            label: 'Map',
            command: (e: any) => this.handleTabChange(MenuDailyReport.Map)
        }
    ];

    public orderMarkers: google.maps.Marker[] = [];
    public map!: google.maps.Map;
    private locationProvider: google.maps.journeySharing.FleetEngineTripLocationProvider | null = null;
    public markerClusterer?: MarkerClusterer;

    public blockedPanel: boolean = false;
    public loadingExportData: boolean = false;

    ngOnInit(): void {
        this.selectedDateRange[1] = new Date();
        this.selectedDateRange[0] = new Date();
        this.selectedDateRange[0].setDate(new Date().getDate() - 30);

        this.getOrdersCompletedAndUnallocated(true);

        this.geoZoneService.selectedGeoZone$.subscribe((value) => {
            this.selectGeoZone = value;
            if (this.menuDailyReportActive === MenuDailyReport.Map) {
                this.startMap();
            }
        });
    }

    public handleFilterChange(event: any) {
        if (this.selectedDateRange[1]) {
            if (this.searchTimer) {
                window.clearTimeout(this.searchTimer);
            }

            this.searchTimer = window.setTimeout(() => {
                this.getOrdersCompletedAndUnallocated();
                this.searchTimer = 0;
            }, 1500);
        }
    }

    public handleVehicleTypeFilterChange(event: any[]) {
        const recentlyAdded = event.find(o => !this.previousSelection.includes(o));
        if (recentlyAdded === RiderVehicleTypeEnum.None) {
            this.filterStatistics.vehicleTypes = !this.appData.isBoracayLocation ?
                [RiderVehicleTypeEnum.None, RiderVehicleTypeEnum.Moto, RiderVehicleTypeEnum.Sedan, RiderVehicleTypeEnum.SUV] :
                [RiderVehicleTypeEnum.None, RiderVehicleTypeEnum.ETrike];
        }

        const diffOnAdd = this.filterStatistics.vehicleTypes.filter((x: any) => !this.previousSelection.includes(x))[0];
        const diffOnRemove = this.previousSelection.filter((x: any) => !this.filterStatistics.vehicleTypes.includes(x))[0];

        if(this.filterStatistics.vehicleTypes.includes(RiderVehicleTypeEnum.None) && diffOnAdd !== RiderVehicleTypeEnum.None){
            this.filterStatistics.vehicleTypes.splice(this.filterStatistics.vehicleTypes.indexOf(RiderVehicleTypeEnum.None), 1);
        }

        if (diffOnRemove === RiderVehicleTypeEnum.None) {
            this.filterStatistics.vehicleTypes = [];
        }

        this.previousSelection = [...event];

        this.handleFilterChange(event);
    }

    private getOrdersCompletedAndUnallocated(firstCall = false) {
        this.blockedPanel = true;

        let vehicleTypes = this.filterStatistics.vehicleTypes;
        if (this.filterStatistics.vehicleTypes.includes(RiderVehicleTypeEnum.None)) {
            vehicleTypes = !this.appData.isBoracayLocation ?
                [RiderVehicleTypeEnum.Moto, RiderVehicleTypeEnum.Sedan, RiderVehicleTypeEnum.SUV] :
                    [ RiderVehicleTypeEnum.ETrike];
        }

        const dto = {
            StartDate: moment(this.selectedDateRange[0]).format('YYYY-MM-DD'),
            EndDate: moment(this.selectedDateRange[1]).format('YYYY-MM-DD'),
            CitiesId: this.filterStatistics.citiesId ? this.filterStatistics.citiesId : null,
            VehicleTypes: vehicleTypes,
            TimeZoneOffset: Utils.getTimezoneOffset()
        };

        this.deliveryOrderService.getOrdersCompletedAndUnallocated(dto, this.messageService)
            .then(data => {
                if (!data.Success) {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                    this.blockedPanel = false;
                    return;
                }
                this.blockedPanel = false;

                this.orderStatistics = data.Items;
                this.geoPoints = data.GeoPoints;
                this.cities = data.Cities;
                if (firstCall) {
                    this.filterStatistics.citiesId = data.Cities
                        .filter((x: any) => x.IsEnabled)
                        .map((x: any) => x.Id);
                }
                this.totalCompletedMoto = data.TotalCompletedMoto;
                this.totalUnallocatedMoto = data.TotalUnallocatedMoto;
                this.totalCompletedSedan = data.TotalCompletedSedan;
                this.totalUnallocatedSedan = data.TotalUnallocatedSedan;
                this.totalCompletedSUV = data.TotalCompletedSUV;
                this.totalUnallocatedSUV = data.TotalUnallocatedSUV;

                if (this.menuDailyReportActive === MenuDailyReport.Map) {
                    this.orderGeoPointMarkers(this.geoPoints);
                }
            })
            .catch(err => {
                this.blockedPanel = false;
                throw err;
            });
    }

    public getOrderStat(orderStatistics: any[], vehicleType: RiderVehicleTypeEnum) {
        return orderStatistics.find(o => o.VehicleType === vehicleType) || { CompletedCount: 0, UnallocatedCount: 0, UnallocatedPercent: 0, Total: 0 };
    }

    public exportOrdersCompletedAndUnallocated(fileType: ExportFileTypeEnum) {
        this.loadingExportData = true;

        const dto = {
            StartDate: moment(this.selectedDateRange[0]).format('YYYY-MM-DD'),
            EndDate: moment(this.selectedDateRange[1]).format('YYYY-MM-DD'),
            CitiesId: this.filterStatistics.citiesId ? this.filterStatistics.citiesId : null,
            VehicleTypes: this.filterStatistics.vehicleTypes,
            FileType: fileType,
            TimeZoneOffset: Utils.getTimezoneOffset()
        };

        this.deliveryOrderService.exportOrdersCompletedAndUnallocated(dto, this.messageService)
            .then(data => {
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Success' });
                this.getFileName();
                Utils.saveFileResponse(data, this.fileName + moment(new Date()).format('DD MMM YYYY hh:mm:ss A'), fileType);
                this.loadingExportData = false;
            })
            .catch(err => {
                this.loadingExportData = false;
                throw err;
            });
    }

    getFileName() {
        const daysDiff = moment(this.selectedDateRange[1]).diff(moment(this.selectedDateRange[0]), 'days');

        if (daysDiff < 7) {
            this.fileName = 'Daily reports ';
        } else if (daysDiff > 6 && daysDiff < 30) {
            this.fileName = 'Weekly reports ';
        } else if (daysDiff > 29 && daysDiff < 365) {
            this.fileName = 'Monthly reports ';
        } else if (daysDiff > 364) {
            this.fileName = 'Yearly reports ';
        }
    }

    handleTabChange(value: MenuDailyReport) {
        this.menuDailyReportActive = value;
        if (this.searchTimer) {
            window.clearTimeout(this.searchTimer);
        }

        this.searchTimer = window.setTimeout(() => {
            if (this.menuDailyReportActive === MenuDailyReport.Map) {
                this.startMap();
            }
            this.searchTimer = 0;
        }, 1500);
    }

    public startMap() {
        const geometry = this.selectGeoZone.PolygonGeometries[0];
        const bounds = new google.maps.LatLngBounds();
        for (const point of this.selectGeoZone.PolygonGeometries) {
            bounds.extend(new google.maps.LatLng(point.Lat, point.Lng));
        }

        this.map = new google.maps.Map(document.getElementById('map') as HTMLElement, {
            zoom: 15,
            center: { lat: geometry.Lat, lng: geometry.Lng },
        });

        this.orderGeoPointMarkers(this.geoPoints);
    }

    private orderGeoPointMarkers(geoPoints: any) {
        this.markerClusterer?.removeMarkers(this.orderMarkers);

        for (const orderMarker of this.orderMarkers) {
            orderMarker.setMap(null);
        }

        this.orderMarkers = [];

        geoPoints.forEach((geoPoint: any) => {
            const marker = new google.maps.Marker({
                position: { lat: geoPoint.Lat, lng: geoPoint.Lng },
                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    fillColor: geoPoint.IsCompleted ? 'green' : 'red',
                    fillOpacity: 1,
                    strokeWeight: 0,
                    scale: 7
                }
            });

            this.orderMarkers.push(marker);
        });

        this.markerClusterer = new MarkerClusterer({ map: this.map, markers: this.orderMarkers });
    }
}
